// Regras puras de Campanhas (modal, relatório, confirmações): estimativa de
// duração do envio, segmentos de progresso, próximas execuções, prévia da
// mensagem com variáveis, status. Sem React/API.
import moment from "moment";
import { i18n } from "../../translate/i18n";

const t = (k, o) => i18n.t(`aeros.campaign.${k}`, o);

// ---------------------------------------------------------------------------
// Planejador de disparos — porta de backend/src/services/CampaignService/
// campaignSchedule.ts. Mesma regra, mesmo resultado: qualquer mudanca la
// precisa ser espelhada aqui. Diferenca unica: sendingDays aqui e array.
// ---------------------------------------------------------------------------
const HORA_RE = /^\d{1,2}:[0-5]\d$/;
const horaConfigurada = (v) => (typeof v === "string" && HORA_RE.test(v.trim()) ? v.trim() : null);
const segundosDoDia = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return h * 3600 + m * 60; };
const DIA_INTEIRO_SEG = 24 * 3600;
const resolveJanela = ({ startHour, endHour }) => {
  const ini = horaConfigurada(startHour), fim = horaConfigurada(endHour);
  const inicioSeg = ini !== null ? segundosDoDia(ini) : 0;
  const fimSeg = fim !== null ? segundosDoDia(fim) : DIA_INTEIRO_SEG;
  return fimSeg <= inicioSeg ? { inicioSeg: 0, fimSeg: DIA_INTEIRO_SEG } : { inicioSeg, fimSeg };
};
const inicioDoDia = (data) => { const x = new Date(data); x.setHours(0, 0, 0, 0); return x; };
const comSegundos = (dia, seg) => new Date(inicioDoDia(dia).getTime() + seg * 1000);
const segDesdeMeiaNoite = (data) => (data.getTime() - inicioDoDia(data).getTime()) / 1000;
const mesmoDia = (a, b) => inicioDoDia(a).getTime() === inicioDoDia(b).getTime();
const diasPermitidos = (sendingDays) => (Array.isArray(sendingDays) ? sendingDays.map(Number) : [0, 1, 2, 3, 4, 5, 6]);
const diaPermitido = (settings, data) => diasPermitidos(settings.sendingDays).includes(data.getDay());
export const normalizaLimiteDiario = (v) => { if (v === null || v === undefined) return 0; const n = Number(String(v).replace(/"/g, "").trim()); return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0; };
// Limite do canal da campanha (espelho de campaignSchedule.ts): SMS usa
// dailyLimitSms; sem ele gravado (null), herda dailyLimit, que e o do WhatsApp.
export const limiteDiarioDoCanal = (settings = {}, canal) =>
  canal === "sms" && settings.dailyLimitSms !== null && settings.dailyLimitSms !== undefined
    ? normalizaLimiteDiario(settings.dailyLimitSms)
    : normalizaLimiteDiario(settings.dailyLimit);

const inicioDaJanelaDoProximoDia = (settings, janela, data) => {
  let dia = inicioDoDia(data);
  for (let i = 0; i < 400; i += 1) {
    dia = new Date(dia.getTime() + 24 * 3600 * 1000);
    dia.setHours(0, 0, 0, 0);
    if (diaPermitido(settings, dia)) return comSegundos(dia, janela.inicioSeg);
  }
  throw new Error("ERR_CAMPAIGN_NO_SENDING_DAY");
};
const posicionaNaJanela = (settings, janela, cursor) => {
  if (!diaPermitido(settings, cursor)) return inicioDaJanelaDoProximoDia(settings, janela, cursor);
  const seg = segDesdeMeiaNoite(cursor);
  if (seg < janela.inicioSeg) return comSegundos(cursor, janela.inicioSeg);
  if (seg > janela.fimSeg) return inicioDaJanelaDoProximoDia(settings, janela, cursor);
  return cursor;
};

export const planejaDisparos = (n, settings = {}, inicio = new Date()) => {
  const total = Math.max(0, Math.floor(Number(n) || 0));
  if (!total) return [];
  const janela = resolveJanela(settings);
  const limite = normalizaLimiteDiario(settings.dailyLimit);
  const intervalo = Math.max(0, Number(settings.messageInterval) || 0);
  const intervaloMaior = Math.max(0, Number(settings.greaterInterval) || 0);
  const limiar = Math.max(0, Number(settings.longerIntervalAfter) || 0);
  const espacamento = limite > 0 ? Math.max((janela.fimSeg - janela.inicioSeg) / limite, intervalo) : 0;
  const plano = [];
  let cursor = posicionaNaJanela(settings, janela, new Date(inicio));
  let enviadosNoDia = 0;
  for (let i = 0; i < total; i += 1) {
    if (limite > 0 && enviadosNoDia >= limite) { cursor = inicioDaJanelaDoProximoDia(settings, janela, cursor); enviadosNoDia = 0; }
    plano.push(new Date(cursor));
    enviadosNoDia += 1;
    const passo = limite > 0 ? espacamento : enviadosNoDia >= limiar ? intervaloMaior : intervalo;
    const reposicionado = posicionaNaJanela(settings, janela, new Date(cursor.getTime() + passo * 1000));
    if (!mesmoDia(reposicionado, cursor)) enviadosNoDia = 0;
    cursor = reposicionado;
  }
  return plano;
};

export const resumoDoPlano = (datas) => {
  if (!datas?.length) return { primeiro: null, ultimo: null, dias: 0 };
  return { primeiro: datas[0], ultimo: datas[datas.length - 1], dias: new Set(datas.map((x) => inicioDoDia(x).getTime())).size };
};

// Duracao do envio em segundos (primeiro ao ultimo disparo planejado).
export const estimateSeconds = (contacts, settings = {}, inicio = new Date()) => {
  const plano = planejaDisparos(contacts, settings, inicio);
  const { primeiro, ultimo } = resumoDoPlano(plano);
  return primeiro ? Math.round((ultimo - primeiro) / 1000) : 0;
};

export const durationLabel = (seconds) => {
  const s = Number(seconds) || 0;
  if (s < 60) return `${s}s`;
  const min = Math.round(s / 60);
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60), m = min % 60;
  if (h < 24) return m ? `${h}h ${m}min` : `${h}h`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

// "1.240 contatos · intervalo 20–60s · ≈ 14h de envio"
// ou, quando atravessa dias: "1.240 contatos · 50/dia · ≈ 25 dias, termina 27/10 às 17:48"
export const estimateLabel = (contacts, settings, inicio = new Date()) => {
  if (!contacts) return "";
  const s = settings || {};
  let plano;
  try { plano = planejaDisparos(contacts, s, inicio); } catch { return t("estimateNoDays"); }
  const { primeiro, ultimo, dias } = resumoDoPlano(plano);
  const contatos = Number(contacts).toLocaleString("pt-BR");
  if (dias <= 1) {
    const { messageInterval = 20, greaterInterval = 60 } = s;
    return t("estimate", { contacts: contatos, min: messageInterval, max: greaterInterval, time: durationLabel(Math.round((ultimo - primeiro) / 1000)) });
  }
  const limite = normalizaLimiteDiario(s.dailyLimit);
  return t("estimateDays", { contacts: contatos, perDay: limite > 0 ? t("perDay", { n: limite }) : t("withinWindow"), days: dias, end: moment(ultimo).format("DD/MM [" + t("at") + "] HH:mm") });
};

// Resumo do limite diario para a dica do modal de configuracoes.
export const dailyLimitSummary = ({ dailyLimit, startHour, endHour, messageInterval, sendingDays }) => {
  // Sem dia marcado nada dispara, com ou sem limite: avisar isso vem antes do "off".
  if (Array.isArray(sendingDays) && sendingDays.length === 0) return { mode: "noDays", gapSeconds: 0, fitPerDay: 0 };
  const limite = normalizaLimiteDiario(dailyLimit);
  if (!limite) return { mode: "off", gapSeconds: 0, fitPerDay: 0 };
  const janela = resolveJanela({ startHour, endHour });
  const duracao = janela.fimSeg - janela.inicioSeg;
  const minimo = Math.max(0, Number(messageInterval) || 0);
  const ideal = duracao / limite;
  if (ideal >= minimo) return { mode: "spread", gapSeconds: Math.round(ideal), fitPerDay: limite };
  return { mode: "floor", gapSeconds: minimo, fitPerDay: minimo > 0 ? Math.floor(duracao / minimo) + 1 : limite };
};

// Segmentos da barra: entregues / falhas / aguardando (enviadas sem entrega) / na fila.
export const progressSegments = (stats) => {
  const s = stats || {};
  const total = Number(s.totalMessages) || 0;
  const delivered = Number(s.deliveredMessages) || 0;
  const failed = Number(s.failedMessages) || 0;
  // awaitingMessages (enviadas sem entrega) vem do backend; sem ele, tudo
  // que não entregou nem falhou conta como "na fila".
  const awaiting = Number(s.awaitingMessages) || 0;
  const sent = delivered + failed + awaiting;
  const queued = Math.max(0, total - sent);
  const pct = (n) => (total ? Math.round((n / total) * 1000) / 10 : 0);
  const resolved = delivered + failed;
  return { total, delivered, failed, awaiting, queued, sent, pctDelivered: pct(delivered), pctFailed: pct(failed), pctAwaiting: pct(awaiting), pctSent: total ? Math.round((sent / total) * 100) : 0, deliveryRate: resolved ? Math.round((delivered / resolved) * 100) : 0 };
};

// Próximas execuções (mesma regra do backend, para campanha nova).
export const nextExecutions = (scheduledAt, { recurrenceType, recurrenceInterval = 1, recurrenceDayOfMonth, recurrenceDaysOfWeek = [] } = {}, count = 4) => {
  if (!scheduledAt || !recurrenceType) return [];
  const out = [];
  let d = moment(scheduledAt);
  const interval = Number(recurrenceInterval) || 1;
  if (recurrenceType === "weekly" && recurrenceDaysOfWeek?.length) {
    const days = [...recurrenceDaysOfWeek].map(Number).sort();
    let cursor = d.clone();
    let guard = 0;
    while (out.length < count && guard < 400) {
      if (days.includes(cursor.day()) && !cursor.isBefore(d)) out.push(cursor.clone());
      cursor = cursor.add(1, "day");
      guard += 1;
    }
    return out;
  }
  for (let i = 0; i < count; i += 1) {
    out.push(d.clone());
    const map = { minutely: [interval, "minutes"], hourly: [interval, "hours"], daily: [interval, "days"], weekly: [interval, "weeks"], biweekly: [interval * 2, "weeks"], monthly: [interval, "months"], yearly: [interval, "years"] };
    const [n, unit] = map[recurrenceType] || [interval, "days"];
    d = d.clone().add(n, unit);
    if (recurrenceType === "monthly" && recurrenceDayOfMonth) d = d.date(Number(recurrenceDayOfMonth));
  }
  return out;
};

// "Próximas: 31/08 · 07/09 · 14/09 · 21/09 às 09:00"
export const nextExecutionsLabel = (dates) => {
  if (!dates?.length) return "";
  const list = dates.map((x) => moment(x));
  return t("next", { dates: list.map((x) => x.format("DD/MM")).join(" · "), time: list[0].format("HH:mm") });
};

export const RECURRENCE_TYPES = ["minutely", "hourly", "daily", "weekly", "biweekly", "monthly", "yearly"].map((key) => ({ key, get label() { return t(`recurrence.${key}`); } }));

export const WEEKDAY_SHORT = () => t("weekdaysShort").split(",");
export const WEEKDAY_LONG = () => t("weekdaysLong").split(",");

// Substitui {nome}, {primeiro_nome}, {numero}, {email}, {saudacao} para a prévia.
export const previewMessage = (text, contactIn, now = new Date()) => {
  const contact = contactIn || {};
  const name = contact.name || t("previewName");
  const h = now.getHours();
  const greeting = t(h < 12 ? "greetingMorning" : h < 18 ? "greetingAfternoon" : "greetingEvening");
  return String(text || "")
    .replace(/{nome}/g, name)
    .replace(/{primeiro_nome}/g, name.split(" ")[0])
    .replace(/{numero}/g, contact.number || "")
    .replace(/{email}/g, contact.email || "")
    .replace(/{saudacao}/g, greeting);
};

export const STATUS_META = {
  INATIVA: { tone: "muted", key: "inactive" },
  PROGRAMADA: { tone: "purple", key: "scheduled" },
  EM_ANDAMENTO: { tone: "info", key: "running" },
  CANCELADA: { tone: "warning", key: "cancelled" },
  FINALIZADA: { tone: "success", key: "finished" },
};
export const statusMeta = (status) => {
  const m = STATUS_META[status] || { tone: "muted", key: "inactive" };
  return { ...m, label: t(`status.${m.key}`) };
};

export const shippingStatus = (item) => (item?.failureReason ? "failed" : item?.deliveredAt ? "delivered" : item?.jobId ? "pending" : "queued");

export const filledMessages = (values) => [1, 2, 3, 4, 5].map((n) => Boolean((values?.[`message${n}`] || "").trim()));

export const formatPhone = (number) => {
  const n = String(number || "").replace(/\D/g, "");
  if (!n) return "";
  if (n.length === 13 && n.startsWith("55")) return `+55 ${n.slice(2, 4)} ${n.slice(4, 9)}-${n.slice(9)}`;
  if (n.length === 12 && n.startsWith("55")) return `+55 ${n.slice(2, 4)} ${n.slice(4, 8)}-${n.slice(8)}`;
  return number;
};

// Campo de URL da midia do cabecalho (template oficial com HEADER IMAGE/
// VIDEO/DOCUMENT). "required" = template exige e esta vazia; "invalid" = sem
// http(s)://; null = ok ou template sem midia. Mesma regra do backend
// (ValidateOficialCampaignService), para o bloqueio acontecer antes do request.
export const erroUrlMidiaCabecalho = (template, url) => {
  if (!template?.headerMidia) return null;
  const valor = (url || "").trim();
  if (!valor) return "required";
  if (!/^https?:\/\/\S+$/i.test(valor)) return "invalid";
  return null;
};
